import { Routes, RouterModule } from '@angular/router';
import { PublicLayoutComponent } from './_layout/public-layout/public-layout.component';
import { PrivateLayoutComponent } from './_layout/private-layout/private-layout.component';
import { AuthGuardUser } from './_guards/auth.guard';
import { RegisterComponent } from './register';
import { LoginComponent } from './login';
import { ChangelogComponent } from './changelog/changelog.component';
import { UserListComponent } from './pages/users/user-list/user-list.component';
import { PageAuthGuard } from './services/auth/page-auth.guard';
import { Error404Component } from './pages/error404/error404.component';
import { GoogleMapComponent } from './shared/google-map/google-map.component';
import { GoogleGeolocationComponent } from './shared/google-geolocation/google-geolocation.component';
import { AuthGuardCheck } from './services/auth/authguardCheck.guard';

const appRoutes: Routes = [
  // Public layout
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      { path: 'register', component: RegisterComponent },
      { path: 'login', component: LoginComponent, canActivate: [AuthGuardCheck] },
      // { path: '', component: LoginComponent },
      { path: '', redirectTo: '/login', pathMatch: 'full' },
    ]
  },
  // Private layout
  {
    path: '',
    component: PrivateLayoutComponent,
    children: [
      { path: 'logout', component: LoginComponent, canActivate: [AuthGuardUser] },
      { path: 'changelog', component: ChangelogComponent, canActivate: [AuthGuardUser] },
      {
        path: 'dashboard', loadComponent: () => import('./pages/dashboard/dashboard.component').then(a => a.DashboardComponent), data: { action: "view", module: "dashboardManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'panchangam-calendar', loadComponent: () => import('./pages/panchangam-calendar/panchangam-calendar.component').then(a => a.PanchangamCalendarComponent), data: { action: "view", module: "dashboardManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'users', component: UserListComponent, canActivate: [PageAuthGuard],
        data: { action: 'view', module: 'userManagement' },
      },
      {
        path: 'users/view/:id', component: UserListComponent, canActivate: [PageAuthGuard],
        data: { action: 'info', module: 'userManagement' },
      },
      {
        path: 'product', loadChildren: () => import('../app/pages/product-management/product-management.module').then(m => m.ProductManagementModule),
        canActivate: [PageAuthGuard],
        data: { action: 'view', module: 'productManagement' },
      },
      {
        path: 'category', loadChildren: () => import('../app/pages/category-management/category-management.module').then(m => m.CategoryManagementModule),
        canActivate: [PageAuthGuard],
        data: { action: 'view', module: 'categoryManagement' },
      },
      {
        path: 'retailer', loadChildren: () => import('../app/pages/retailer-management/retailer-management.module').then(m => m.RetailerManagementModule),
        canActivate: [PageAuthGuard],
        data: { action: 'view', module: 'retailerManagement' },
      },
      {
        path: 'retailer/orders', loadComponent: () => import('./pages/retailer-management/retailer-orders/retailer-orders.component').then(a => a.RetailerOrdersComponent), data: { action: "view", module: "retailerOrderManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'retailer/order/:id', loadComponent: () => import('./pages/retailer-management/retailer-view-order/retailer-view-order.component').then(a => a.RetailerViewOrderComponent), data: { action: "info", module: "retailerOrderManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier/all', loadComponent: () => import('./pages/supplier-management/supplier-list/supplier-list.component').then(a => a.SupplierListComponent), data: { action: "view", module: "supplierManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier/create', loadComponent: () => import('./pages/supplier-management/supplier-form/supplier-form.component').then(a => a.SupplierFormComponent), data: { action: "add", module: "supplierManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier/update/:id', loadComponent: () => import('./pages/supplier-management/supplier-form/supplier-form.component').then(a => a.SupplierFormComponent), data: { action: "update", module: "supplierManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier/view/:id', loadComponent: () => import('./pages/supplier-management/supplier-form/supplier-form.component').then(a => a.SupplierFormComponent), data: { action: "view", module: "supplierManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-agreements/all', loadComponent: () => import('./pages/supplier-management/supplier-agreement-list/supplier-agreement-list.component').then(a => a.SupplierAgreementListComponent), data: { action: "view", module: "supplierAgreementManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-agreements/create', loadComponent: () => import('./pages/supplier-management/supplier-agreement-form/supplier-agreement-form.component').then(a => a.SupplierAgreementFormComponent), data: { action: "add", module: "supplierAgreementManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-agreements/update/:id', loadComponent: () => import('./pages/supplier-management/supplier-agreement-form/supplier-agreement-form.component').then(a => a.SupplierAgreementFormComponent), data: { action: "update", module: "supplierAgreementManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-agreements/view/:id', loadComponent: () => import('./pages/supplier-management/supplier-agreement-form/supplier-agreement-form.component').then(a => a.SupplierAgreementFormComponent), data: { action: "view", module: "supplierAgreementManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-orders/all', loadComponent: () => import('./pages/supplier-management/supplier-order-list/supplier-order-list.component').then(a => a.SupplierOrderListComponent), data: { action: "view", module: "supplierOrderManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-orders/view/:id', loadComponent: () => import('./pages/supplier-management/supplier-order-view/supplier-order-view.component').then(a => a.SupplierOrderViewComponent), data: { action: "view", module: "supplierOrderManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-invoices/all', loadComponent: () => import('./pages/supplier-management/supplier-invoice-list/supplier-invoice-list.component').then(a => a.SupplierInvoiceListComponent), data: { action: "view", module: "supplierInvoiceManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-invoices/view/:id', loadComponent: () => import('./pages/supplier-management/supplier-invoice-view/supplier-invoice-view.component').then(a => a.SupplierInvoiceViewComponent), data: { action: "view", module: "supplierInvoiceManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-credit-notes/all', loadComponent: () => import('./pages/supplier-management/supplier-credit-note-list/supplier-credit-note-list.component').then(a => a.SupplierCreditNoteListComponent), data: { action: "view", module: "supplierCreditNoteManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-credit-notes/view/:id', loadComponent: () => import('./pages/supplier-management/supplier-credit-note-view/supplier-credit-note-view.component').then(a => a.SupplierCreditNoteViewComponent), data: { action: "view", module: "supplierCreditNoteManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-settlements/all', loadComponent: () => import('./pages/supplier-management/supplier-settlement-list/supplier-settlement-list.component').then(a => a.SupplierSettlementListComponent), data: { action: "view", module: "supplierSettlementManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'supplier-settlements/view/:id', loadComponent: () => import('./pages/supplier-management/supplier-settlement-view/supplier-settlement-view.component').then(a => a.SupplierSettlementViewComponent), data: { action: "view", module: "supplierSettlementManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'offer', loadChildren: () => import('../app/pages/offer-management/offer-management.module').then(m => m.OfferManagementModule),
        canActivate: [PageAuthGuard],
        data: { action: 'view', module: 'offerManagement' },
      },
      {
        path: 'monthlypass', loadChildren: () => import('../app/pages/monthly-pass/monthly-pass.module').then(m => m.MonthlyPassModule),
        canActivate: [PageAuthGuard],
        data: { action: 'view', module: 'monthlypassManagement' },
      },
      {
        path: 'zone/all', loadComponent: () => import('../app/pages/zone-management/list-zone/list-zone.component').then(a => a.ListZoneComponent)
        , data: { action: "view", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/create', loadComponent: () => import('../app/pages/zone-management/add-zone/add-zone.component').then(a => a.AddZoneComponent), data: { action: "add", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/update/:id', loadComponent: () => import('../app/pages/zone-management/add-zone/add-zone.component').then(a => a.AddZoneComponent), data: { action: "update", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/states/all', loadComponent: () => import('./pages/zone-management/state-list/state-list.component').then(a => a.StateListComponent), data: { action: "view", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/states/create', loadComponent: () => import('./pages/zone-management/add-edit-state/add-edit-state.component').then(a => a.AddEditStateComponent), data: { action: "add", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/states/update/:id', loadComponent: () => import('./pages/zone-management/add-edit-state/add-edit-state.component').then(a => a.AddEditStateComponent), data: { action: "update", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/regions/all', loadComponent: () => import('./pages/zone-management/region-list/region-list.component').then(a => a.RegionListComponent), data: { action: "view", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/regions/create', loadComponent: () => import('./pages/zone-management/add-edit-region/add-edit-region.component').then(a => a.AddEditRegionComponent), data: { action: "add", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/regions/update/:id', loadComponent: () => import('./pages/zone-management/add-edit-region/add-edit-region.component').then(a => a.AddEditRegionComponent), data: { action: "update", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/districts/all', loadComponent: () => import('./pages/zone-management/district-list/district-list.component').then(a => a.DistrictListComponent), data: { action: "view", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/districts/create', loadComponent: () => import('./pages/zone-management/add-edit-district/add-edit-district.component').then(a => a.AddEditDistrictComponent), data: { action: "add", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/districts/update/:id', loadComponent: () => import('./pages/zone-management/add-edit-district/add-edit-district.component').then(a => a.AddEditDistrictComponent), data: { action: "update", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/cities/all', loadComponent: () => import('./pages/zone-management/city-list/city-list.component').then(a => a.CityListComponent), data: { action: "view", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/cities/create', loadComponent: () => import('./pages/zone-management/add-edit-city/add-edit-city.component').then(a => a.AddEditCityComponent), data: { action: "add", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/cities/update/:id', loadComponent: () => import('./pages/zone-management/add-edit-city/add-edit-city.component').then(a => a.AddEditCityComponent), data: { action: "update", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/areas/all', loadComponent: () => import('./pages/zone-management/area-list/area-list.component').then(a => a.AreaListComponent), data: { action: "view", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/areas/create', loadComponent: () => import('./pages/zone-management/add-edit-area/add-edit-area.component').then(a => a.AddEditAreaComponent), data: { action: "add", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'zone/areas/update/:id', loadComponent: () => import('./pages/zone-management/add-edit-area/add-edit-area.component').then(a => a.AddEditAreaComponent), data: { action: "update", module: "zoneManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'product-promotion/all', loadComponent: () => import('./pages/product-promotions/product-promotion-list/product-promotion-list.component').then(a => a.ProductPromotionListComponent)
        , data: { action: "view", module: "productPromotionsManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'product-promotion/create', loadComponent: () => import('./pages/product-promotions/add-edit-product-promotion/add-edit-product-promotion.component').then(a => a.AddEditProductPromotionComponent), data: { action: "add", module: "productPromotionsManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'product-promotion/update/:id', loadComponent: () => import('./pages/product-promotions/add-edit-product-promotion/add-edit-product-promotion.component').then(a => a.AddEditProductPromotionComponent), data: { action: "update", module: "productPromotionsManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'delivery-agent/all', loadComponent: () => import('./pages/delivery-agent/delivery-agent-list/delivery-agent-list.component').then(a => a.DeliveryAgentListComponent)
        , data: { action: "view", module: "deliveryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'delivery-agent/create', loadComponent: () => import('./pages/delivery-agent/add-edit-delivery-agent/add-edit-delivery-agent.component').then(a => a.AddEditDeliveryAgentComponent), data: { action: "add", module: "deliveryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'delivery-agent/update/:id', loadComponent: () => import('./pages/delivery-agent/add-edit-delivery-agent/add-edit-delivery-agent.component').then(a => a.AddEditDeliveryAgentComponent), data: { action: "update", module: "deliveryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'user-representative/all', loadComponent: () => import('./pages/user-representative/user-representative-list/user-representative-list.component').then(a => a.UserRepresentativeListComponent), data: { action: "view", module: "userRepManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'user-representative/create', loadComponent: () => import('./pages/user-representative/add-edit-user-representative/add-edit-user-representative.component').then(a => a.AddEditUserRepresentativeComponent), data: { action: "add", module: "userRepManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'user-representative/update/:id', loadComponent: () => import('./pages/user-representative/add-edit-user-representative/add-edit-user-representative.component').then(a => a.AddEditUserRepresentativeComponent), data: { action: "update", module: "userRepManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'service-provider/all', loadComponent: () => import('./pages/service-provider-management/service-provider-list/service-provider-list.component').then(a => a.ServiceProviderListComponent), data: { action: "view", module: "serviceProviderManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'service-provider/create', loadComponent: () => import('./pages/service-provider-management/add-edit-service-provider/add-edit-service-provider.component').then(a => a.AddEditServiceProviderComponent), data: { action: "add", module: "serviceProviderManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'service-provider/update/:id', loadComponent: () => import('./pages/service-provider-management/add-edit-service-provider/add-edit-service-provider.component').then(a => a.AddEditServiceProviderComponent), data: { action: "update", module: "serviceProviderManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'service-booking/all', loadComponent: () => import('./pages/service-booking-management/service-booking-list/service-booking-list.component').then(a => a.ServiceBookingListComponent), data: { action: "view", module: "serviceProviderManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'service-booking/view/:id', loadComponent: () => import('./pages/service-booking-management/service-booking-view/service-booking-view.component').then(a => a.ServiceBookingViewComponent), data: { action: "view", module: "serviceProviderManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'service/all', loadComponent: () => import('./pages/service-management/service-list/service-list.component').then(a => a.ServiceListComponent), data: { action: "view", module: "serviceManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'service/create', loadComponent: () => import('./pages/service-management/add-edit-service/add-edit-service.component').then(a => a.AddEditServiceComponent), data: { action: "add", module: "serviceManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'service/update/:id', loadComponent: () => import('./pages/service-management/add-edit-service/add-edit-service.component').then(a => a.AddEditServiceComponent), data: { action: "update", module: "serviceManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/dashboard', loadComponent: () => import('./pages/devotional-library-management/devotional-library-dashboard/devotional-library-dashboard.component').then(a => a.DevotionalLibraryDashboardComponent), data: { action: "view", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/comics/all', loadComponent: () => import('./pages/devotional-library-management/comic-series-list/comic-series-list.component').then(a => a.ComicSeriesListComponent), data: { action: "view", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/comics/create', loadComponent: () => import('./pages/devotional-library-management/add-edit-comic-series/add-edit-comic-series.component').then(a => a.AddEditComicSeriesComponent), data: { action: "add", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/comics/update/:id', loadComponent: () => import('./pages/devotional-library-management/add-edit-comic-series/add-edit-comic-series.component').then(a => a.AddEditComicSeriesComponent), data: { action: "update", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/books/all', loadComponent: () => import('./pages/devotional-library-management/book-list/book-list.component').then(a => a.BookListComponent), data: { action: "view", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/books/create', loadComponent: () => import('./pages/devotional-library-management/add-edit-book/add-edit-book.component').then(a => a.AddEditBookComponent), data: { action: "add", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/books/update/:id', loadComponent: () => import('./pages/devotional-library-management/add-edit-book/add-edit-book.component').then(a => a.AddEditBookComponent), data: { action: "update", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/stotrams/all', loadComponent: () => import('./pages/devotional-library-management/stotram-list/stotram-list.component').then(a => a.StotramListComponent), data: { action: "view", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/stotrams/create', loadComponent: () => import('./pages/devotional-library-management/add-edit-stotram/add-edit-stotram.component').then(a => a.AddEditStotramComponent), data: { action: "add", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/stotrams/update/:id', loadComponent: () => import('./pages/devotional-library-management/add-edit-stotram/add-edit-stotram.component').then(a => a.AddEditStotramComponent), data: { action: "update", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/deity-groups/all', loadComponent: () => import('./pages/devotional-library-management/deity-group-list/deity-group-list.component').then(a => a.DeityGroupListComponent), data: { action: "view", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/deity-groups/create', loadComponent: () => import('./pages/devotional-library-management/add-edit-deity-group/add-edit-deity-group.component').then(a => a.AddEditDeityGroupComponent), data: { action: "add", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-library/deity-groups/update/:id', loadComponent: () => import('./pages/devotional-library-management/add-edit-deity-group/add-edit-deity-group.component').then(a => a.AddEditDeityGroupComponent), data: { action: "update", module: "devotionalLibraryManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/dashboard', loadComponent: () => import('./pages/devotional-events-management/devotional-events-dashboard/devotional-events-dashboard.component').then(a => a.DevotionalEventsDashboardComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/types/all', loadComponent: () => import('./pages/devotional-events-management/event-type-list/event-type-list.component').then(a => a.EventTypeListComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/types/create', loadComponent: () => import('./pages/devotional-events-management/add-edit-event-type/add-edit-event-type.component').then(a => a.AddEditEventTypeComponent), data: { action: "add", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/types/update/:id', loadComponent: () => import('./pages/devotional-events-management/add-edit-event-type/add-edit-event-type.component').then(a => a.AddEditEventTypeComponent), data: { action: "update", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/organizers/all', loadComponent: () => import('./pages/devotional-events-management/event-organizer-list/event-organizer-list.component').then(a => a.EventOrganizerListComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/organizers/create', loadComponent: () => import('./pages/devotional-events-management/add-view-event-organizer/add-view-event-organizer.component').then(a => a.AddViewEventOrganizerComponent), data: { action: "add", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/organizers/view/:id', loadComponent: () => import('./pages/devotional-events-management/add-view-event-organizer/add-view-event-organizer.component').then(a => a.AddViewEventOrganizerComponent), data: { action: "update", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/validators/all', loadComponent: () => import('./pages/devotional-events-management/ticket-validator-list/ticket-validator-list.component').then(a => a.TicketValidatorListComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/validators/create', loadComponent: () => import('./pages/devotional-events-management/add-view-ticket-validator/add-view-ticket-validator.component').then(a => a.AddViewTicketValidatorComponent), data: { action: "add", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/validators/view/:id', loadComponent: () => import('./pages/devotional-events-management/add-view-ticket-validator/add-view-ticket-validator.component').then(a => a.AddViewTicketValidatorComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/all', loadComponent: () => import('./pages/devotional-events-management/devotional-event-list/devotional-event-list.component').then(a => a.DevotionalEventListComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/view/:id', loadComponent: () => import('./pages/devotional-events-management/devotional-event-view/devotional-event-view.component').then(a => a.DevotionalEventViewComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/bookings/all', loadComponent: () => import('./pages/devotional-events-management/devotional-event-booking-list/devotional-event-booking-list.component').then(a => a.DevotionalEventBookingListComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/bookings/view/:id', loadComponent: () => import('./pages/devotional-events-management/devotional-event-booking-view/devotional-event-booking-view.component').then(a => a.DevotionalEventBookingViewComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/invoices/all', loadComponent: () => import('./pages/devotional-events-management/devotional-event-invoice-list/devotional-event-invoice-list.component').then(a => a.DevotionalEventInvoiceListComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'devotional-events/invoices/view/:id', loadComponent: () => import('./pages/devotional-events-management/devotional-event-invoice-view/devotional-event-invoice-view.component').then(a => a.DevotionalEventInvoiceViewComponent), data: { action: "view", module: "devotionalEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple/dashboard', loadComponent: () => import('./pages/temple-management/temple-dashboard/temple-dashboard.component').then(a => a.TempleDashboardComponent), data: { action: "view", module: "templeManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temples/all', loadComponent: () => import('./pages/temple-management/temple-list/temple-list.component').then(a => a.TempleListComponent), data: { action: "view", module: "templeManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temples/create', loadComponent: () => import('./pages/temple-management/temple-form/temple-form.component').then(a => a.TempleFormComponent), data: { action: "add", module: "templeManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temples/update/:id', loadComponent: () => import('./pages/temple-management/temple-form/temple-form.component').then(a => a.TempleFormComponent), data: { action: "update", module: "templeManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temples/view/:id', loadComponent: () => import('./pages/temple-management/temple-form/temple-form.component').then(a => a.TempleFormComponent), data: { action: "view", module: "templeManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-poojas/all', loadComponent: () => import('./pages/temple-management/temple-pooja-list/temple-pooja-list.component').then(a => a.TemplePoojaListComponent), data: { action: "view", module: "templePoojaManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-poojas/create', loadComponent: () => import('./pages/temple-management/temple-pooja-form/temple-pooja-form.component').then(a => a.TemplePoojaFormComponent), data: { action: "add", module: "templePoojaManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-poojas/update/:id', loadComponent: () => import('./pages/temple-management/temple-pooja-form/temple-pooja-form.component').then(a => a.TemplePoojaFormComponent), data: { action: "update", module: "templePoojaManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-poojas/view/:id', loadComponent: () => import('./pages/temple-management/temple-pooja-form/temple-pooja-form.component').then(a => a.TemplePoojaFormComponent), data: { action: "view", module: "templePoojaManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-events/all', loadComponent: () => import('./pages/temple-management/temple-event-list/temple-event-list.component').then(a => a.TempleEventListComponent), data: { action: "view", module: "templeEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-events/create', loadComponent: () => import('./pages/temple-management/temple-event-form/temple-event-form.component').then(a => a.TempleEventFormComponent), data: { action: "add", module: "templeEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-events/update/:id', loadComponent: () => import('./pages/temple-management/temple-event-form/temple-event-form.component').then(a => a.TempleEventFormComponent), data: { action: "update", module: "templeEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-events/view/:id', loadComponent: () => import('./pages/temple-management/temple-event-form/temple-event-form.component').then(a => a.TempleEventFormComponent), data: { action: "view", module: "templeEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-bookings/all', loadComponent: () => import('./pages/temple-management/temple-booking-list/temple-booking-list.component').then(a => a.TempleBookingListComponent), data: { action: "view", module: "templeBookingManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-bookings/offline/create', loadComponent: () => import('./pages/temple-management/temple-offline-ticket-form/temple-offline-ticket-form.component').then(a => a.TempleOfflineTicketFormComponent), data: { action: "add", module: "templeBookingManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-bookings/view/:id', loadComponent: () => import('./pages/temple-management/temple-booking-view/temple-booking-view.component').then(a => a.TempleBookingViewComponent), data: { action: "view", module: "templeBookingManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-tickets/all', loadComponent: () => import('./pages/temple-management/temple-ticket-list/temple-ticket-list.component').then(a => a.TempleTicketListComponent), data: { action: "view", module: "templeTicketManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'temple-tickets/view/:id', loadComponent: () => import('./pages/temple-management/temple-ticket-view/temple-ticket-view.component').then(a => a.TempleTicketViewComponent), data: { action: "view", module: "templeTicketManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/profile', loadComponent: () => import('./pages/devotional-events-management/eventadmin-profile/eventadmin-profile.component').then(a => a.EventAdminProfileComponent), data: { action: "view", module: "eventOrganizerProfileManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/profile', loadComponent: () => import('./pages/temple-management/templeadmin-profile/templeadmin-profile.component').then(a => a.TempleAdminProfileComponent), data: { action: "view", module: "templeAdminProfileManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/poojas/all', loadComponent: () => import('./pages/temple-management/temple-pooja-list/temple-pooja-list.component').then(a => a.TemplePoojaListComponent), data: { action: "view", module: "templeAdminPoojaManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/poojas/create', loadComponent: () => import('./pages/temple-management/temple-pooja-form/temple-pooja-form.component').then(a => a.TemplePoojaFormComponent), data: { action: "add", module: "templeAdminPoojaManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/poojas/update/:id', loadComponent: () => import('./pages/temple-management/temple-pooja-form/temple-pooja-form.component').then(a => a.TemplePoojaFormComponent), data: { action: "update", module: "templeAdminPoojaManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/poojas/view/:id', loadComponent: () => import('./pages/temple-management/temple-pooja-form/temple-pooja-form.component').then(a => a.TemplePoojaFormComponent), data: { action: "view", module: "templeAdminPoojaManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/events/all', loadComponent: () => import('./pages/temple-management/temple-event-list/temple-event-list.component').then(a => a.TempleEventListComponent), data: { action: "view", module: "templeAdminEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/events/create', loadComponent: () => import('./pages/temple-management/temple-event-form/temple-event-form.component').then(a => a.TempleEventFormComponent), data: { action: "add", module: "templeAdminEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/events/update/:id', loadComponent: () => import('./pages/temple-management/temple-event-form/temple-event-form.component').then(a => a.TempleEventFormComponent), data: { action: "update", module: "templeAdminEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/events/view/:id', loadComponent: () => import('./pages/temple-management/temple-event-form/temple-event-form.component').then(a => a.TempleEventFormComponent), data: { action: "view", module: "templeAdminEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/validators/all', loadComponent: () => import('./pages/devotional-events-management/ticket-validator-list/ticket-validator-list.component').then(a => a.TicketValidatorListComponent), data: { action: "view", module: "templeAdminTicketValidatorManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/validators/create', loadComponent: () => import('./pages/devotional-events-management/add-view-ticket-validator/add-view-ticket-validator.component').then(a => a.AddViewTicketValidatorComponent), data: { action: "add", module: "templeAdminTicketValidatorManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/validators/view/:id', loadComponent: () => import('./pages/devotional-events-management/add-view-ticket-validator/add-view-ticket-validator.component').then(a => a.AddViewTicketValidatorComponent), data: { action: "view", module: "templeAdminTicketValidatorManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/bookings/all', loadComponent: () => import('./pages/temple-management/temple-booking-list/temple-booking-list.component').then(a => a.TempleBookingListComponent), data: { action: "view", module: "templeAdminBookingManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/bookings/offline/create', loadComponent: () => import('./pages/temple-management/temple-offline-ticket-form/temple-offline-ticket-form.component').then(a => a.TempleOfflineTicketFormComponent), data: { action: "add", module: "templeAdminBookingManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/bookings/view/:id', loadComponent: () => import('./pages/temple-management/temple-booking-view/temple-booking-view.component').then(a => a.TempleBookingViewComponent), data: { action: "view", module: "templeAdminBookingManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/tickets/all', loadComponent: () => import('./pages/temple-management/temple-ticket-list/temple-ticket-list.component').then(a => a.TempleTicketListComponent), data: { action: "view", module: "templeAdminTicketManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'templeadmin/tickets/view/:id', loadComponent: () => import('./pages/temple-management/temple-ticket-view/temple-ticket-view.component').then(a => a.TempleTicketViewComponent), data: { action: "view", module: "templeAdminTicketManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/events/all', loadComponent: () => import('./pages/devotional-events-management/devotional-event-list/devotional-event-list.component').then(a => a.DevotionalEventListComponent), data: { action: "view", module: "eventOrganizerEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/events/create', loadComponent: () => import('./pages/devotional-events-management/eventadmin-event-editor/eventadmin-event-editor.component').then(a => a.EventAdminEventEditorComponent), data: { action: "add", module: "eventOrganizerEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/events/update/:id', loadComponent: () => import('./pages/devotional-events-management/eventadmin-event-editor/eventadmin-event-editor.component').then(a => a.EventAdminEventEditorComponent), data: { action: "update", module: "eventOrganizerEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/events/view/:id', loadComponent: () => import('./pages/devotional-events-management/devotional-event-view/devotional-event-view.component').then(a => a.DevotionalEventViewComponent), data: { action: "view", module: "eventOrganizerEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/validators/all', loadComponent: () => import('./pages/devotional-events-management/ticket-validator-list/ticket-validator-list.component').then(a => a.TicketValidatorListComponent), data: { action: "view", module: "eventOrganizerTicketValidatorManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/validators/create', loadComponent: () => import('./pages/devotional-events-management/add-view-ticket-validator/add-view-ticket-validator.component').then(a => a.AddViewTicketValidatorComponent), data: { action: "add", module: "eventOrganizerTicketValidatorManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/validators/view/:id', loadComponent: () => import('./pages/devotional-events-management/add-view-ticket-validator/add-view-ticket-validator.component').then(a => a.AddViewTicketValidatorComponent), data: { action: "view", module: "eventOrganizerTicketValidatorManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/bookings/all', loadComponent: () => import('./pages/devotional-events-management/devotional-event-booking-list/devotional-event-booking-list.component').then(a => a.DevotionalEventBookingListComponent), data: { action: "view", module: "eventOrganizerBookingManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/events/:id/bookings', loadComponent: () => import('./pages/devotional-events-management/devotional-event-booking-list/devotional-event-booking-list.component').then(a => a.DevotionalEventBookingListComponent), data: { action: "view", module: "eventOrganizerBookingManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/bookings/view/:id', loadComponent: () => import('./pages/devotional-events-management/devotional-event-booking-view/devotional-event-booking-view.component').then(a => a.DevotionalEventBookingViewComponent), data: { action: "view", module: "eventOrganizerBookingManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/invoices/all', loadComponent: () => import('./pages/devotional-events-management/devotional-event-invoice-list/devotional-event-invoice-list.component').then(a => a.DevotionalEventInvoiceListComponent), data: { action: "view", module: "eventOrganizerEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'eventadmin/invoices/view/:id', loadComponent: () => import('./pages/devotional-events-management/devotional-event-invoice-view/devotional-event-invoice-view.component').then(a => a.DevotionalEventInvoiceViewComponent), data: { action: "view", module: "eventOrganizerEventManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/dashboard', loadComponent: () => import('./pages/subscription-management/subscription-dashboard/subscription-dashboard.component').then(a => a.SubscriptionDashboardComponent), data: { action: "view", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/tiers/all', loadComponent: () => import('./pages/subscription-management/subscription-tier-list/subscription-tier-list.component').then(a => a.SubscriptionTierListComponent), data: { action: "view", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/tiers/create', loadComponent: () => import('./pages/subscription-management/add-edit-subscription-tier/add-edit-subscription-tier.component').then(a => a.AddEditSubscriptionTierComponent), data: { action: "add", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/tiers/update/:id', loadComponent: () => import('./pages/subscription-management/add-edit-subscription-tier/add-edit-subscription-tier.component').then(a => a.AddEditSubscriptionTierComponent), data: { action: "update", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/plans/all', loadComponent: () => import('./pages/subscription-management/subscription-plan-list/subscription-plan-list.component').then(a => a.SubscriptionPlanListComponent), data: { action: "view", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/plans/create', loadComponent: () => import('./pages/subscription-management/add-edit-subscription-plan/add-edit-subscription-plan.component').then(a => a.AddEditSubscriptionPlanComponent), data: { action: "add", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/plans/update/:id', loadComponent: () => import('./pages/subscription-management/add-edit-subscription-plan/add-edit-subscription-plan.component').then(a => a.AddEditSubscriptionPlanComponent), data: { action: "update", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/benefit-rules/all', loadComponent: () => import('./pages/subscription-management/subscription-benefit-rule-list/subscription-benefit-rule-list.component').then(a => a.SubscriptionBenefitRuleListComponent), data: { action: "view", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/benefit-rules/create', loadComponent: () => import('./pages/subscription-management/add-edit-subscription-benefit-rule/add-edit-subscription-benefit-rule.component').then(a => a.AddEditSubscriptionBenefitRuleComponent), data: { action: "add", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/benefit-rules/update/:id', loadComponent: () => import('./pages/subscription-management/add-edit-subscription-benefit-rule/add-edit-subscription-benefit-rule.component').then(a => a.AddEditSubscriptionBenefitRuleComponent), data: { action: "update", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'subscriptions/users/all', loadComponent: () => import('./pages/subscription-management/user-subscription-list/user-subscription-list.component').then(a => a.UserSubscriptionListComponent), data: { action: "view", module: "subscriptionManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'telecaller/all', loadComponent: () => import('./pages/telecaller/telecaller-lits/telecaller-lits.component').then(a => a.TelecallerLitsComponent), data: { action: "view", module: "telecallerManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'telecaller/create', loadComponent: () => import('./pages/telecaller/add-edit-telecaller/add-edit-telecaller.component').then(a => a.AddEditTelecallerComponent), data: { action: "add", module: "telecallerManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'telecaller/update/:id', loadComponent: () => import('./pages/telecaller/add-edit-telecaller/add-edit-telecaller.component').then(a => a.AddEditTelecallerComponent), data: { action: "update", module: "telecallerManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'payment/all', loadComponent: () => import('./pages/payments-management/payment-list/payment-list.component').then(a => a.PaymentListComponent)
        , data: { action: "view", module: "paymentManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'payment/view/:id', loadComponent: () => import('./pages/payments-management/view-payment/view-payment.component').then(a => a.ViewPaymentComponent), data: { action: "info", module: "paymentManagement" },
        canActivate: [PageAuthGuard],
      },

      {
        path: 'order/all', loadComponent: () => import('./pages/order-management/order-list/order-list.component').then(a => a.OrderListComponent)
        , data: { action: "view", module: "orderManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'order/view/:id', loadComponent: () => import('./pages/order-management/view-order/view-order.component').then(a => a.ViewOrderComponent), data: { action: "info", module: "orderManagement" },
        canActivate: [PageAuthGuard],
      },

      {
        path: 'coupons/all', loadComponent: () => import('./pages/coupon-management/coupon-list/coupon-list.component').then(a => a.CouponListComponent), data: { action: "view", module: "couponManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'coupon/create', loadComponent: () => import('./pages/coupon-management/add-edit-coupon/add-edit-coupon.component').then(a => a.AddEditCouponComponent), data: { action: "add", module: "couponManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'coupon/update/:id', loadComponent: () => import('./pages/coupon-management/add-edit-coupon/add-edit-coupon.component').then(a => a.AddEditCouponComponent), data: { action: "update", module: "couponManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'coupon/user-rep/:userId/:userrepId/:zone/create', loadComponent: () => import('./pages/coupon-management/add-edit-coupon/add-edit-coupon.component').then(a => a.AddEditCouponComponent), data: { action: "add", module: "userRepManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'coupon/user-rep/:userId/:userrepId/:zone/update/:id', loadComponent: () => import('./pages/coupon-management/add-edit-coupon/add-edit-coupon.component').then(a => a.AddEditCouponComponent), data: { action: "update", module: "userRepManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'fee/all', loadComponent: () => import('./pages/fee-management/fee-list/fee-list.component').then(a => a.FeeListComponent), data: { action: "view", module: "feeManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'fee/create', loadComponent: () => import('./pages/fee-management/add-edit-fee/add-edit-fee.component').then(a => a.AddEditFeeComponent), data: { action: "add", module: "feeManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'fee/update/:id', loadComponent: () => import('./pages/fee-management/add-edit-fee/add-edit-fee.component').then(a => a.AddEditFeeComponent), data: { action: "update", module: "feeManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'incentive-rule/all', loadComponent: () => import('./pages/incentiverule-management/incentiverule-list/incentiverule-list.component').then(a => a.IncentiveruleListComponent), data: { action: "view", module: "incentiveManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'incentive-rule/create', loadComponent: () => import('./pages/incentiverule-management/add-edit-incentiverule/add-edit-incentiverule.component').then(a => a.AddEditIncentiveruleComponent), data: { action: "add", module: "incentiveManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'incentive-rule/update/:id', loadComponent: () => import('./pages/incentiverule-management/add-edit-incentiverule/add-edit-incentiverule.component').then(a => a.AddEditIncentiveruleComponent), data: { action: "update", module: "incentiveManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'rakeemart-product/all', loadComponent: () => import('./pages/rakeemart-product-management/rakeemart-product-list/rakeemart-product-list.component').then(a => a.RakeemartProductListComponent), data: { action: "view", module: "inhouseProductManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'rakeemart-product/create', loadComponent: () => import('./pages/rakeemart-product-management/rakeemart-product-add/rakeemart-product-add.component').then(a => a.RakeemartProductAddComponent), data: { action: "add", module: "inhouseProductManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'rakeemart-product/update/:id', loadComponent: () => import('./pages/rakeemart-product-management/rakeemart-product-add/rakeemart-product-add.component').then(a => a.RakeemartProductAddComponent), data: { action: "update", module: "inhouseProductManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'bulk-upload', loadComponent: () => import('./pages/bulk-upload/bulk-upload-list/bulk-upload-list.component').then(a => a.BulkUploadListComponent), data: { action: "view", module: "userManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'add-bulk-upload', loadComponent: () => import('./pages/bulk-upload/add-bulk-upload-list/add-bulk-upload-list.component').then(a => a.AddBulkUploadListComponent), data: { action: "view", module: "userManagement" },
        canActivate: [PageAuthGuard],
      },
      {
        path: 'bulk-upload-details/:id', loadComponent: () => import('./pages/bulk-upload/bulk-upload-details/bulk-upload-details.component').then(a => a.BulkUploadDetailsComponent), data: { action: "view", module: "userManagement" },
        canActivate: [PageAuthGuard],
      },

    ],
  },
  { path: '404', component: Error404Component },
  { path: 'map', component: GoogleMapComponent },
  { path: 'map2', component: GoogleGeolocationComponent },
  // otherwise redirect to home
  { path: '**', redirectTo: '404' }
];

export const routing = RouterModule.forRoot(appRoutes, { scrollOffset: [0, 0], scrollPositionRestoration: 'top' });

